import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Skeleton,
  Descriptions,
  Button,
  Form,
  Modal,
  Input,
  Tooltip,
  message as antMessage,
  Select,
  Upload,
  DatePicker,
  Divider,
  Typography
} from 'antd';
import {
  CheckOutlined,
  UploadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import useAuthContext from '../../../contexts/AuthContext';
import SimpleResourceLandingLayout from '../../../components/layouts/SimpleResourceLandingLayout';
import useErrorMessage from '../../../utils/ErrorMessage';
import useColumns from './columns';

const MyComplaints = () => {
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const [forceRefresh, setForceRefresh] = useState(false);
  const resourceName = 'reclamations';
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(true);
  const [reclamation, setReclamation] = useState();
  const [reclamations, setReclamations] = useState();
  const [loading, setLoading] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [footer, setFooter] = useState([]);
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState();
  const [form] = Form.useForm();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const patchReclamation = async (reclamationId, formData) => {
    try {
      if (reclamationId) {
        const { status } = await dispatchAPI('PATCH', {
          url: `reclamations/${reclamationId && reclamationId}`,
          body: formData
        });
        if (status === 200) {
          antMessage.success(t('Réclamation mise à jour'));
          setIsModalVisible(false);
          setForceRefresh(!forceRefresh);
          setFileList();
        }
      }
      form.resetFields();
    } catch (e) {
      if (e.response) antMessage.error(e.response.status);
    }
  };

  const handleOk = () => {
    const values = form.getFieldsValue();
    const { traitement, isProcessed, recallDate } = values;
    let data;

    if (isProcessed) {
      data = {
        traitement,
        isProcessed,
        recallDate,
        status: 'VALID'
      };
    } else {
      data = {
        traitement,
        isProcessed,
        recallDate,
        status: 'IN_PROGRESS'
      };
    }
    try {
      const formData = new FormData();
      if (values.files) {
        for (let i = 0; i < values.files.length; i += 1) {
          formData.append('files', values.files[i]);
        }
      }
      formData.append('values', JSON.stringify(data));
      patchReclamation(reclamation[0]?._id, formData);
    } catch (e) {
      if (e.response) antMessage(e.response.data.message);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setFileList();
  };

  const patchReclamationInProgress = async (reclamationId) => {
    try {
      if (reclamationId) {
        const { data } = await dispatchAPI('GET', {
          url: `reclamations/?_id=${
            reclamationId && reclamationId
          }&populate=contratId`
        });
        if (data[0]?.status === 'WAITING') {
          const values = JSON.stringify({
            status: 'IN_PROGRESS'
          });
          const response = await dispatchAPI('PATCH', {
            url: `reclamations/${reclamationId && reclamationId}`,
            body: {
              values
            }
          });
          if (response.status === 200) {
            setForceRefresh(!forceRefresh);
            antMessage.success(t('Réclamation mise à jour'));
          }
        } else {
          antMessage.error(t('Réclamation déjà validée'));
        }
      }
    } catch (e) {
      if (e.response) antMessage.error(e.response.status);
    }
  };

  const traiterReclamation = async (reclamationId) => {
    try {
      if (reclamationId) {
        setLoading(true);
        showModal();
        setForceRefresh(!forceRefresh);
        const { data } = await dispatchAPI('GET', {
          url: `reclamations/?_id=${
            reclamationId && reclamationId
          }&populate=contratId,files,acFiles`
        });
        setReclamation(data);
        setLoading(false);
      }
    } catch (e) {
      if (e.response) antMessage.error(e.response.status);
    }
  };

  const columns = useColumns(
    patchReclamationInProgress,
    traiterReclamation,
    enums,
    reclamations
  );

  const setfooter = () => {
    if (reclamation && reclamation[0]?.status !== 'CLOSED') {
      setFooter([
        <Button key="Valider" onClick={handleOk}>
          Valider
        </Button>,
        <Button key="Annuler" onClick={() => handleCancel()}>
          Annuler
        </Button>
      ]);
    } else {
      setFooter([
        <Button key="Valider" onClick={() => handleCancel()}>
          Annuler
        </Button>
      ]);
    }
  };
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const initFileList = (files) => {
    if (files === undefined || files === null || files.length === 0) return;
    const files_list = [];
    for (let i = 0; i < files.length; i += 1) {
      files_list.push({
        uid: files[i]._id,
        status: 'done',
        name: files[i].metadata.originalName
      });
    }
    setFileList(files_list);
  };

  const downloadFile = async (file) => {
    if (file === null) return;
    try {
      const response = await dispatchAPI('GET', {
        url: `/reclamations/files/${reclamation?.[0]?._id}/${file._id}`,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: response.data.type
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.metadata.originalName}`;
      a.click();
    } catch (e) {
      if (e.response) antMessage(e.response.status);
    }
  };

  const getReclamations = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/reclamations?insurer=${user?._id}&populate=contratId`
      });
      const array = [];
      await data?.map((d) => array.push(d.contratId.number));

      const uniqueArray = (ary) => ary.filter((v, i) => ary.indexOf(v) === i);

      setReclamations(
        uniqueArray(array).map((number) => ({
          number,
          _id: data.find((d) => d.contratId.number === number).contratId._id
        }))
      );
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/reclamations/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    (async () => {
      await getReclamations();
    })();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    (async () => {
      await getEnums();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (reclamation) {
      initFileList(reclamation.acFiles);
    }
  }, [reclamation]);

  useEffect(() => {
    setfooter();
    // eslint-disable-next-line
  }, [reclamation]);

  return (
    <div>
      <>
        <Modal
          title="Détails"
          open={isModalVisible}
          okText="Valider"
          width={900}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={footer}
          confirmLoading={loading}
        >
          <Skeleton loading={loading}>
            {reclamation && (
              <Descriptions>
                <Descriptions.Item span={3} label="Contrat">
                  {reclamation && reclamation[0]?.contratId?.number}
                </Descriptions.Item>
                <Descriptions.Item span={3} label="Objet">
                  {reclamation && reclamation[0]?.objetReclamation}
                </Descriptions.Item>
                <Descriptions.Item span={3} label="Commentaire">
                  {reclamation && reclamation[0]?.commentaire}
                </Descriptions.Item>
                {reclamation && reclamation[0]?.files[0] && (
                  <Descriptions.Item
                    span={3}
                    label={t('Piece(s) Jointe(s) Assurmax')}
                  >
                    {reclamation &&
                      reclamation[0]?.files.map((file) => {
                        return (
                          <Tooltip
                            placement="topLeft"
                            title={`${file && file.metadata.originalName}`}
                          >
                            <Button
                              style={{ marginLeft: -16 }}
                              type="link"
                              onClick={() => downloadFile(file)}
                            >
                              <UploadOutlined />
                              {file && file.metadata.originalName}
                            </Button>
                          </Tooltip>
                        );
                      })}
                  </Descriptions.Item>
                )}
                {reclamation && reclamation[0].traitement && (
                  <Descriptions.Item label="réponse apportée" span={3}>
                    {reclamation && reclamation[0]?.traitement}
                  </Descriptions.Item>
                )}
                {reclamation && reclamation[0].acFiles?.length && (
                  <Descriptions.Item
                    span={3}
                    label={t('Piece(s) Jointe(s) en réponse')}
                  >
                    {reclamation &&
                      reclamation[0]?.acFiles.map((file) => {
                        return (
                          <Tooltip
                            placement="topLeft"
                            title={`${file && file.metadata.originalName}`}
                          >
                            <Button
                              style={{ marginLeft: -16 }}
                              type="link"
                              onClick={() => downloadFile(file)}
                            >
                              <UploadOutlined />
                              {file && file.metadata.originalName}
                            </Button>
                          </Tooltip>
                        );
                      })}
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
            {reclamation && reclamation[0]?.status !== 'VALID' && (
              <div>
                <Divider plain style={{ marginBottom: 30 }}>
                  <Typography.Title level={5}>Traitement</Typography.Title>
                </Divider>
                <Form
                  form={form}
                  initialValues={{
                    recallDate: moment().add(1, 'day')
                  }}
                >
                  <Form.Item
                    style={{ display: 'flex', flexDirection: 'column' }}
                    name="traitement"
                    label="Réponse à apporter"
                    rules={[
                      { required: true, message: t('errors.form.REQUIRED') }
                    ]}
                  >
                    <Input.TextArea />
                  </Form.Item>
                  <Form.Item
                    style={{ display: 'flex', flexDirection: 'column' }}
                    name="files"
                    label={t('Piece(s) jointe(s')}
                    getValueFromEvent={normFile}
                  >
                    <Upload
                      maxCount={5}
                      fileList={fileList}
                      onChange={(info) => {
                        const newFileList = [...info.fileList];
                        setFileList(newFileList);
                      }}
                      beforeUpload={(file) => {
                        const newFiles = fileList;
                        newFiles.push(file);
                        setFileList(newFiles);
                        return false;
                      }}
                      onRemove={(file) => {
                        const index = fileList.indexOf(file);
                        const newFileList = fileList.slice();
                        newFileList.splice(index, 1);
                        setFileList(newFileList);
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        style={{ marginTop: 10 }}
                      >
                        Cliquez pour télécharger
                      </Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item
                    style={{ display: 'flex', flexDirection: 'column' }}
                    name="isProcessed"
                    label="Avez-vous traité cette réclamation ?"
                    rules={[
                      { required: true, message: t('errors.form.REQUIRED') }
                    ]}
                  >
                    <Select
                      onChange={(value) => {
                        if (value) {
                          setDatePickerVisible(true);
                        } else {
                          setDatePickerVisible(false);
                        }
                      }}
                    >
                      <Select.Option value>
                        <CheckOutlined
                          style={{ color: 'green', fontSize: 16 }}
                        />
                        <span style={{ marginLeft: 10 }}>
                          {t('reclamations.confirmation')}
                        </span>
                      </Select.Option>
                      <Select.Option value={false}>
                        <InfoCircleOutlined
                          style={{ color: 'blue', fontSize: 16 }}
                        />
                        <span style={{ marginLeft: 10 }}>
                          {t('reclamations.information')}
                        </span>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="recallDate"
                    label="Date de rappel"
                    hidden={datePickerVisible}
                    rules={[
                      {
                        required: { datePickerVisible }
                      }
                    ]}
                  >
                    <DatePicker
                      format="YYYY-MM-DD"
                      disabledDate={(current) => {
                        return current && current <= moment();
                      }}
                    />
                  </Form.Item>
                </Form>
              </div>
            )}
          </Skeleton>
        </Modal>
      </>

      <SimpleResourceLandingLayout
        resourceName={resourceName}
        populate="contratId"
        extraQuery={`insurer=${user?.insurer?._id}`}
        columns={columns}
        forceRefresh={forceRefresh}
        customActionColumn
        onDoubleClickAction={false}
        withCreateButton={false}
      />
    </div>
  );
};

export default MyComplaints;
